import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import adminApi from '../../lib/adminApi';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Building2, Check, X, LifeBuoy } from 'lucide-react';

/**
 * Ascoltatore globale del Portale Gestore: controlla in tempo quasi reale se un
 * locale assegnato ha inviato una richiesta di assistenza e mostra un pop-up
 * Accetta/Rifiuta ovunque ci si trovi nel portale.
 */
export const GestoreIncomingSupportModal = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [req, setReq] = useState(null);
  const shownRef = useRef(new Set());

  const poll = useCallback(async () => {
    try {
      const { data } = await adminApi.get('/remote/support/manager/incoming');
      const list = Array.isArray(data) ? data : [];
      if (list.length === 0) { setReq(null); return; }
      const first = list[0];
      setReq((cur) => (cur && cur.id === first.id ? cur : first));
      list.forEach((r) => {
        if (!shownRef.current.has(r.id)) {
          shownRef.current.add(r.id);
          toast.info(`${r.restaurant_name}: ${t('gis.newRequest', { defaultValue: 'richiesta di assistenza' })}`);
        }
      });
    } catch (e) { /* noop */ }
  }, [t]);

  useEffect(() => {
    poll();
    const id = setInterval(poll, 3000);
    return () => clearInterval(id);
  }, [poll]);

  const respond = async (action) => {
    if (!req) return;
    try {
      const { data } = await adminApi.post(`/remote/support/manager/${req.id}/respond`, { action });
      setReq(null);
      if (action === 'accepted') {
        toast.success(t('gis.accepted', { defaultValue: 'Richiesta accettata' }));
        navigate(data?.session_id ? `/gestore/assistenza-remota/sessione/${data.session_id}` : '/gestore/assistenza-remota');
      } else {
        toast(t('gis.declined', { defaultValue: 'Richiesta rifiutata' }));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('gis.error', { defaultValue: 'Errore' }));
    }
  };

  return (
    <Dialog open={!!req} onOpenChange={(o) => { if (!o) setReq(null); }}>
      <DialogContent data-testid="gestore-incoming-support-modal" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LifeBuoy className="h-5 w-5 text-primary" /> {t('gis.title', { defaultValue: 'Richiesta di assistenza' })}
          </DialogTitle>
        </DialogHeader>
        {req && (
          <div className="flex items-center gap-3 rounded-xl border border-border p-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0"><Building2 className="h-5 w-5" /></span>
            <div className="min-w-0">
              <div className="font-semibold truncate">{req.restaurant_name}</div>
              <div className="text-sm text-muted-foreground truncate">{req.requested_by_name}{req.message ? ` · "${req.message}"` : ''}</div>
            </div>
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" className="border-rose-500/40 text-rose-600" onClick={() => respond('declined')} data-testid="gis-decline"><X className="h-4 w-4 mr-1" /> {t('gis.decline', { defaultValue: 'Rifiuta' })}</Button>
          <Button onClick={() => respond('accepted')} data-testid="gis-accept"><Check className="h-4 w-4 mr-1" /> {t('gis.accept', { defaultValue: 'Accetta' })}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
